import React, { useState, useEffect } from 'react';
import { Settings, Save, Clock, MapPin, Users, Star, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '@/services/api';

const ConfiguracoesPlataforma = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    tempo_minimo_liberacao: 5,
    max_entregas_visiveis: 3,
    raio_maximo_km: 10.0,
    distancia_minima_novos_pedidos_metros: 500,
    peso_tempo_entrega: 25,
    peso_avaliacao_cliente: 30,
    peso_indice_reclamacao: 25,
    peso_nota_admin: 20
  });
  
  useEffect(() => {
    carregarConfiguracoes();
  }, []);
  
  const carregarConfiguracoes = async () => {
    try {
      const response = await api.get('/configuracoes');
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };
  
  const salvarConfiguracoes = async () => {
    // Validar soma dos pesos
    const somaPesos = config.peso_tempo_entrega + config.peso_avaliacao_cliente + 
                      config.peso_indice_reclamacao + config.peso_nota_admin;
    
    if (somaPesos !== 100) {
      toast.error(`A soma dos pesos deve ser 100%. Atual: ${somaPesos}%`);
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('rota017_token');
      await api.put('/configuracoes', config, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };
  
  const somaPesos = config.peso_tempo_entrega + config.peso_avaliacao_cliente + 
                    config.peso_indice_reclamacao + config.peso_nota_admin;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
            <Settings className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configurações da Plataforma</h1>
            <p className="text-sm text-gray-500">Gerencie as configurações globais do sistema de entregas</p>
          </div>
        </div>
        
        <Button 
          onClick={salvarConfiguracoes}
          disabled={saving || somaPesos !== 100}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configurações de Tempo e Visibilidade */}
        <div className="bg-white rounded-2xl border p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-500" />
            Tempo e Visibilidade
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tempo Mínimo de Liberação (minutos)
              </label>
              <Input
                type="number"
                min="0"
                max="60"
                value={config.tempo_minimo_liberacao}
                onChange={(e) => setConfig({...config, tempo_minimo_liberacao: parseInt(e.target.value) || 0})}
                className="max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tempo que a entrega fica "invisível" após criação antes de aparecer para entregadores
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Máximo de Entregas Visíveis
              </label>
              <Input
                type="number"
                min="1"
                max="10"
                value={config.max_entregas_visiveis}
                onChange={(e) => setConfig({...config, max_entregas_visiveis: parseInt(e.target.value) || 3})}
                className="max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Quantidade máxima de entregas mostradas para cada entregador
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Raio Máximo (km)
              </label>
              <Input
                type="number"
                min="1"
                max="50"
                step="0.5"
                value={config.raio_maximo_km}
                onChange={(e) => setConfig({...config, raio_maximo_km: parseFloat(e.target.value) || 10})}
                className="max-w-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Distância máxima entre entregador e origem da entrega
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Distância mínima do último estabelecimento (metros)
              </label>
              <Input
                type="number"
                min="0"
                max="10000"
                step="50"
                value={config.distancia_minima_novos_pedidos_metros ?? 500}
                onChange={(e) => setConfig({...config, distancia_minima_novos_pedidos_metros: parseInt(e.target.value) || 0})}
                className="max-w-xs"
                data-testid="input-dist-minima"
              />
              <p className="text-xs text-gray-500 mt-1">
                Após finalizar uma entrega, o entregador só recebe novos pedidos DO MESMO estabelecimento após se afastar essa distância. Use 0 para desabilitar. Recomendado: 500m.
              </p>
            </div>
          </div>
        </div>
        
        {/* Pesos do Ranking */}
        <div className="bg-white rounded-2xl border p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Pesos do Ranking
          </h2>
          
          {/* Indicador de soma */}
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
            somaPesos === 100 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {somaPesos === 100 ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-500" />
            )}
            <span className={`text-sm font-medium ${somaPesos === 100 ? 'text-green-700' : 'text-red-700'}`}>
              Soma dos pesos: {somaPesos}% {somaPesos !== 100 && '(deve ser 100%)'}
            </span>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tempo de Entrega ({config.peso_tempo_entrega}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={config.peso_tempo_entrega}
                onChange={(e) => setConfig({...config, peso_tempo_entrega: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Menor tempo médio de entrega = melhor pontuação
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Avaliação dos Clientes ({config.peso_avaliacao_cliente}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={config.peso_avaliacao_cliente}
                onChange={(e) => setConfig({...config, peso_avaliacao_cliente: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maior média de estrelas (1-5) = melhor pontuação
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Índice de Reclamação ({config.peso_indice_reclamacao}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={config.peso_indice_reclamacao}
                onChange={(e) => setConfig({...config, peso_indice_reclamacao: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Menor índice de reclamações = melhor pontuação
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nota do Admin ({config.peso_nota_admin}%)
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={config.peso_nota_admin}
                onChange={(e) => setConfig({...config, peso_nota_admin: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maior nota dada pelo admin = melhor pontuação
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview do Ranking */}
      <div className="bg-white rounded-2xl border p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-500" />
          Como o Ranking Funciona
        </h2>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <Clock className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <p className="font-medium text-orange-900">Tempo de Entrega</p>
            <p className="text-2xl font-bold text-orange-600">{config.peso_tempo_entrega}%</p>
            <p className="text-xs text-orange-700">Benchmark: 30 min = 100 pontos</p>
          </div>
          
          <div className="bg-yellow-50 rounded-xl p-4 text-center">
            <Star className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="font-medium text-yellow-900">Estrelas</p>
            <p className="text-2xl font-bold text-yellow-600">{config.peso_avaliacao_cliente}%</p>
            <p className="text-xs text-yellow-700">5 estrelas = 100 pontos</p>
          </div>
          
          <div className="bg-red-50 rounded-xl p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="font-medium text-red-900">Reclamações</p>
            <p className="text-2xl font-bold text-red-600">{config.peso_indice_reclamacao}%</p>
            <p className="text-xs text-red-700">0% reclamações = 100 pontos</p>
          </div>
          
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <Users className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="font-medium text-purple-900">Nota Admin</p>
            <p className="text-2xl font-bold text-purple-600">{config.peso_nota_admin}%</p>
            <p className="text-xs text-purple-700">Nota 5 = 100 pontos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracoesPlataforma;
